import React, { useState, useCallback } from "react";
import { Stack, Title, Text } from "@mantine/core";

import { useVenues, useDisplayActivations } from "@/hooks";
import { Display } from "@/utils/api";

import { LoadingIndicator } from "@/components/generic/LoadingIndicator";

import { VenueDisplaySection } from "../VenueDisplaySection";
import { DisplayLinkSuccess, DisplayLinkError } from "../../DisplayActivations";
import { useApp } from "@/contexts";

export interface VenueDisplayListProps {
  /** Activation code from URL params */
  activationCode: string;
}

export const VenueDisplayList: React.FC<VenueDisplayListProps> = ({
  activationCode,
}) => {
  const { isAuthenticated, user } = useApp();
  const [linkingDisplayId, setLinkingDisplayId] = useState<string | null>(null);
  const [linkedDisplay, setLinkedDisplay] = useState<Display | null>(null);

  // Load venues with displays
  const {
    venues,
    loading: venuesLoading,
    lastError: venuesError,
    isEmpty,
  } = useVenues({
    autoLoad: true,
    isAuthenticated,
    userId: user?.id,
  });

  // Display activation hook for linking
  const {
    link,
    loading: linkingLoading,
    lastError: linkError,
    reset: linkReset,
  } = useDisplayActivations({
    mode: "single", // Using single mode for linking operations
    autoLoad: false, // We'll manually trigger link operations
  });

  // Handle display linking
  const handleLinkDisplay = useCallback(
    async (display: Display) => {
      if (!display.id) {
        return;
      }

      setLinkingDisplayId(display.id);

      const result = await link({
        activationCode,
        displayId: display.id,
      });

      if (result.didSucceed()) {
        setLinkedDisplay(display);
      }

      setLinkingDisplayId(null);
    },
    [activationCode, link]
  );

  const handleTryAgainClick = useCallback(() => {
    setLinkedDisplay(null);
    linkReset();
  }, []);

  // Show success view when display is linked
  if (linkedDisplay) {
    return (
      <DisplayLinkSuccess
        display={linkedDisplay}
        message="Display successfully linked! You can now close this page."
      />
    );
  }

  // Show error if there's a linking error
  if (linkError) {
    return (
      <DisplayLinkError
        title="Link Failed"
        message={linkError}
        actionText="Try Again"
        onAction={handleTryAgainClick}
      />
    );
  }

  // Show error if there's a venues loading error
  if (venuesError) {
    return (
      <DisplayLinkError title="Failed to Load Venues" message={venuesError} />
    );
  }

  // Show loading state
  if (venuesLoading) {
    return (
      <Stack align="center" gap="lg" style={{ padding: "2rem" }}>
        <LoadingIndicator size="lg" color="blue" />
        <Text size="lg" c="dimmed">
          Loading venues and displays...
        </Text>
      </Stack>
    );
  }

  // Show empty state
  if (isEmpty) {
    return (
      <DisplayLinkError
        title="No Displays Available!"
        message="No venues or displays were found. Please contact your administrator."
      />
    );
  }

  // Filter venues that have displays
  const venuesWithDisplays = venues.filter(
    (venue) => venue.displays && venue.displays.length > 0
  );

  if (venuesWithDisplays.length === 0) {
    return (
      <DisplayLinkError
        title="No Displays Available"
        message="No displays were found in any venues. Please contact your administrator."
      />
    );
  }

  // Main venue/display list view
  return (
    <Stack gap="xl">
      <div>
        <Title order={2} size="h3" c="gray.8" mb="xs">
          Select Display to Link
        </Title>
        <Text size="md" c="dimmed">
          Choose a display to link with activation code:{" "}
          <Text span fw={600} c="blue.6">
            {activationCode}
          </Text>
        </Text>
      </div>

      <Stack gap="lg">
        {venuesWithDisplays.map((venue) => (
          <VenueDisplaySection
            key={venue.id}
            venue={venue}
            onLinkDisplay={handleLinkDisplay}
            isLinking={linkingLoading}
            linkingDisplayId={linkingDisplayId}
          />
        ))}
      </Stack>
    </Stack>
  );
};
